/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Query hook for fetching saved views
 */

import { createApiClient, isApiFailure } from "@/lib/api/api-client";
import { QUERY_STALE_TIME } from "@/lib/constants/query-config";
import { useQuery } from "@tanstack/react-query";
import type { SavedViewType, SavedViewsResponse } from "./types";
import { getSavedViewsEndpoint } from "@/lib/utils/saved-views/endpoints";

interface UseSavedViewsQueryOptions {
  viewType: SavedViewType;
  organizationId: string | null;
  includeOrgPresets: boolean;
  enabled: boolean;
  isLoaded: boolean;
  isSignedIn: boolean;
  getToken: () => Promise<string | null>;
}

/**
 * Hook for fetching saved views from the server
 */
export function useSavedViewsQuery({
  viewType,
  organizationId,
  includeOrgPresets,
  enabled,
  isLoaded,
  isSignedIn,
  getToken,
}: UseSavedViewsQueryOptions) {
  const queryKey = ["unified-saved-views", organizationId, viewType];
  const apiEndpoint = getSavedViewsEndpoint(viewType);

  return useQuery<SavedViewsResponse>({
    queryKey,
    enabled:
      enabled &&
      isLoaded &&
      isSignedIn &&
      Boolean(organizationId) &&
      (viewType === "projects" ||
        viewType === "clients" ||
        viewType === "suppliers"),
    queryFn: async () => {
      if (!organizationId) throw new Error("Organization not found");

      const api = createApiClient({
        getToken,
        getOrganizationId: () => organizationId,
        autoRetry429: true,
      });

      if (viewType === "projects") {
        // Projects API
        const searchParams = new URLSearchParams({
          viewType: "projects",
          includeOrgPresets: String(includeOrgPresets),
        });

        const response = await api.get<Array<unknown>>(
          `${apiEndpoint}?${searchParams.toString()}`,
        );

        if (isApiFailure(response)) {
          throw new Error(response.message || "Failed to fetch saved views");
        }

        const viewsArray = Array.isArray(response.data) ? response.data : [];

        // Convert to SavedViewsResponse format
        return {
          personal: viewsArray.filter(
            (v: unknown): v is SavedViewsResponse["personal"][number] => {
              return (
                typeof v === "object" &&
                v !== null &&
                !("is_org_preset" in v && v.is_org_preset)
              );
            },
          ),
          orgPresets: viewsArray.filter(
            (v: unknown): v is SavedViewsResponse["orgPresets"][number] => {
              return (
                typeof v === "object" &&
                v !== null &&
                "is_org_preset" in v &&
                v.is_org_preset === true
              );
            },
          ),
        };
      } else if (viewType === "suppliers") {
        // Suppliers API - similar to projects (returns array)
        const searchParams = new URLSearchParams({
          viewType: "suppliers",
          includeOrgPresets: String(includeOrgPresets),
        });

        const response = await api.get<Array<unknown>>(
          `${apiEndpoint}?${searchParams.toString()}`,
        );

        if (isApiFailure(response)) {
          throw new Error(response.message || "Failed to fetch saved views");
        }

        const viewsArray = Array.isArray(response.data) ? response.data : [];

        // Convert to SavedViewsResponse format
        return {
          personal: viewsArray.filter(
            (v: unknown): v is SavedViewsResponse["personal"][number] => {
              return (
                typeof v === "object" &&
                v !== null &&
                !("is_org_preset" in v && v.is_org_preset)
              );
            },
          ),
          orgPresets: viewsArray.filter(
            (v: unknown): v is SavedViewsResponse["orgPresets"][number] => {
              return (
                typeof v === "object" &&
                v !== null &&
                "is_org_preset" in v &&
                v.is_org_preset === true
              );
            },
          ),
        };
      } else {
        // Clients API (returns { personal, orgPresets } format)
        const searchParams = new URLSearchParams({
          view_type: viewType,
          include_org_presets: String(includeOrgPresets),
          include_personal: "true",
        });

        const response = await api.get<SavedViewsResponse>(
          `${apiEndpoint}?${searchParams.toString()}`,
        );

        if (isApiFailure(response)) {
          throw new Error(response.message || "Failed to fetch saved views");
        }

        const apiData = response.data;
        if (
          apiData &&
          typeof apiData === "object" &&
          "personal" in apiData &&
          "orgPresets" in apiData
        ) {
          return apiData as SavedViewsResponse;
        }

        throw new Error("Unexpected response format from saved views API");
      }
    },
    staleTime: QUERY_STALE_TIME.STANDARD,
  });
}
